import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import { useTracking } from "../hooks/useTracking";
import { ActionCategory } from "../services/logging/logService";
import axios from "axios";
import Select from "../components/Select";
import BeatLoader from "react-spinners/BeatLoader";
import { useSelector } from "react-redux";
import AsyncSelect from "react-select/async";
import { handleLogout } from "../config/axios";
import ArticleCodeSelect from "../components/ArticleCodeSelect";
import decryptData from "../decrypt";

/**
 * Funzione per registrare direttamente le azioni di ricerca senza usare il sistema di debounce
 */
const logSearchDirectly = (action: string, description: string, metadata: any) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No auth token found, cannot log search');
      return;
    }
    
    // Crea un payload di log simile a quello usato dal logService, ma con ID univoco
    const logMessage = {
      action: `search:${action}`,
      description,
      timestamp: new Date().toISOString(),
      pagePath: window.location.pathname,
      sessionId: `direct_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      metadata: {
        ...metadata,
        directLog: true,
        timestamp: new Date().toISOString()
      }
    };
    
    // Payload completo per l'API
    const payload = {
      level: 'info',
      serviceName: 'api',
      message: logMessage,
    };
    
    // Chiamata diretta all'API senza debounce
    axios.post('/nodeapi/writeLog', payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    .then(() => {
      console.log(`Log di ricerca inviato direttamente: ${action}`);
    })
    .catch(error => {
      console.error('Errore nell\'invio diretto del log:', error);
    });
  } catch (error) {
    console.error('Errore nella preparazione del log diretto:', error);
  }
};

export default function Disponibilita() {
  const formikRef = useRef(null);
  const tracking = useTracking();
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [availabilityData, setAvailabilityData] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [agentFilters, setAgentFilters] = useState([]);
  const [unfilteredData, setUnfilteredData] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(() => {
    const savedFilters = sessionStorage.getItem("disponibilita-filters");
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      return parsedFilters.selectedAgent || null;
    }
    return null;
  });
  const [codeOptions, setCodeOptions] = useState([]); // popoliamo sin da subito

  const navigate = useNavigate();
  const tableRef = useRef(null);
  const theme = useSelector((state: any) => state.menu.theme);

  // Opzioni magazzino
  const warehouseOptions = [
    { value: "C1", label: "Casoria" },
    { value: "D1", label: "Caserta" },
    { value: "U1", label: "Pesaro" },
    { value: "T1", label: "Barberino" },
    { value: "P1", label: "Pescara" },
    { value: "CRD", label: "CRD" },
  ];

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await fetch("/nodeapi/articoli", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          page: 1,
          pageSize: 100,
        }),
      });

            if (response.status !== 200 && response.status !== 201) {
              handleLogout(
                response,
                "/nodeapi/articoli",
                JSON.stringify({
                  page: 1,
                  pageSize: 100,
                })
              );
              return;
            }

 
      const result = await response.json();
      const decrypted = decryptData(
        result.encryptedData,
        result.encryptedKey,
        result.iv
      );
      const decryptedJson = JSON.parse(decrypted);
      if (decryptedJson.data) {
        setAllArticles(decryptedJson.data); // Salviamo tutti gli articoli per la ricerca locale
      }
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch availability data
  const fetchAvailability = async (
    description,
    magazzinoArray = [],
    currentPage = 1,
    codiceArticolo = ""
  ) => {
    try {
      setLoading(true);
      // /nodeapi/
      const response = await fetch("/nodeapi/disponibilita", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          codice_articolo: codiceArticolo || "",
          descrizione: description || "",
          magazzino: magazzinoArray,
        }),
      });

            if (response.status !== 200   && response.status !== 201) {
              handleLogout(
                response,
                "/nodeapi/disponibilita",
                JSON.stringify({
                  page: 1,
                  pageSize: 100,
                })
              );
              return;
            }

      const result = await response.json();

      const decrypted = decryptData(
        result.encryptedData,
        result.encryptedKey,
        result.iv
      );
      const decryptedJson = JSON.parse(decrypted);

 

      if (decryptedJson.status === "processed") {
        const data = decryptedJson.queryResult;
        setUnfilteredData(data); // Salviamo i dati non filtrati
        console.log(data);

        // Salviamo sempre tutti i dati non filtrati
        setAvailabilityData(data);
        setUnfilteredData(data);

        setTotalPages(decryptedJson.pagination.totalPages);
        setPage(decryptedJson.pagination.currentPage);
      } else {
        setAvailabilityData(null);
        setUnfilteredData(null);
      }
    } catch (error) {
      console.error("Error fetching availability:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchArticles();
  }, []);

  // Applica il filtro magazzino quando unfilteredData cambia o quando viene selezionato un nuovo magazzino
  useEffect(() => {
    if (unfilteredData && unfilteredData.length > 0) {
      const magazzino = formikRef.current?.values?.magazzino;
      if (magazzino) {
        const filteredData = unfilteredData.filter((item) => {
          const itemMagazzino = item.MAGAZZINO?.toUpperCase() || "";
          const selectedValue = magazzino.value?.toUpperCase() || "";
          
          // Mappa il codice al nome completo per il confronto
          const mappedValue = switchDeposito(selectedValue);
          
          // Confronta con il nome mappato
          return itemMagazzino === mappedValue.toUpperCase();
        });
        setAvailabilityData(filteredData);
      } else {
        setAvailabilityData(unfilteredData);
      }
    }
  }, [unfilteredData]);

  const searchArticles = async (searchText: string) => {
    try {
      const response = await fetch("/nodeapi/articoli", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          page: 1,
          pageSize: 100, // Show up to 100 results
          search: {
            descrizione_articolo: searchText,
          },
        }),
      });

      

            if (response.status !== 200 && response.status !== 201) {
              handleLogout(
                response,
                "/nodeapi/articoli",
                JSON.stringify({
                  page: 1,
                  pageSize: 100,
                })
              );
              return;
            }

      const result = await response.json();
      const decrypted = decryptData(
        result.encryptedData,
        result.encryptedKey,
        result.iv
      );
      const decryptedJson = JSON.parse(decrypted);
      return decryptedJson.data || [];
    } catch (error) {
      console.error("Error searching articles:", error);
      return [];
    }
  };

  const handleSearch = async () => {
    const { article, magazzino, codice_articolo } = formikRef.current.values;

    // Always prioritize the article's code if available
    const selectedCode = article?.value || codice_articolo?.value || "";
    const articleDesc = article?.label || "";

    // 1. Track search action with standard tracking system
    tracking.trackSearch('Ricerca Disponibilità ' + new Date().getTime(), undefined, {
      filters: {
        article: article ? { value: article.value, label: article.label } : null,
        magazzino: magazzino ? { value: magazzino.value, label: magazzino.label } : null,
        codice_articolo: codice_articolo ? { value: codice_articolo.value, label: codice_articolo.label } : null
      },
      userRole,
      timestamp: new Date().toISOString()
    });
    
    // 2. Track search action with direct method
    logSearchDirectly('disponibilita_search', 'Ricerca Disponibilità (Direct Log)', {
      filters: {
        article: article ? { value: article.value, label: article.label } : null,
        magazzino: magazzino ? { value: magazzino.value, label: magazzino.label } : null,
        codice_articolo: codice_articolo ? { value: codice_articolo.value, label: codice_articolo.label } : null
      },
      userRole,
      timestamp: new Date().toISOString()
    });

    // Only proceed with search if either article or codice_articolo is selected
    if (selectedCode || articleDesc) {
      // Non passiamo il magazzino al backend, recuperiamo tutti i dati
      await fetchAvailability("", [], 1, selectedCode);
    }
  };

  const handlePageChange = async (newPage) => {
    const { article, magazzino, codice_articolo } = formikRef.current.values;
    const articleDesc = article?.label || "";
    const magazzinoVal = magazzino ? [magazzino.value] : [];
    const codiceArtVal = codice_articolo?.value || "";
    
    // Log page change action
    logSearchDirectly('disponibilita_page_change', 'Cambio Pagina Disponibilità (Direct Log)', {
      filters: {
        article: article ? { value: article.value, label: article.label } : null,
        magazzino: magazzino ? { value: magazzino.value, label: magazzino.label } : null,
        codice_articolo: codice_articolo ? { value: codice_articolo.value, label: codice_articolo.label } : null,
        page: newPage
      },
      userRole,
      timestamp: new Date().toISOString()
    });
    
    await fetchAvailability(articleDesc, magazzinoVal, newPage, codiceArtVal);
  };

  const clearFilters = (setFieldValue) => {
    // 1. Log clear filters action with standard system
/*     tracking.logAction(
      ActionCategory.USER_ACTION,
      "clear_filters",
      "User cleared all filters in Disponibilità",
      {
        userRole,
        timestamp: new Date().toISOString()
      }
    ); */

    // 2. Log clear filters with direct method
    logSearchDirectly('disponibilita_clear_filters', 'Reset Filtri Disponibilità (Direct Log)', {
      userRole,
      timestamp: new Date().toISOString()
    });
    
    setFieldValue("article", null);
    setFieldValue("codice_agente", null);
    setFieldValue("magazzino", null);
    setFieldValue("codice_articolo", null);
    setSelectedAgent(null);
    setAvailabilityData(null);
    setUnfilteredData(null);
    sessionStorage.removeItem("disponibilita-filters");
  };

  const Pagination = () => {
    const maxVisiblePages = 3;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return (
      <div className="flex justify-center items-center mt-4 mb-8">
        <button
          onClick={() => handlePageChange(1)}
          disabled={page === 1}
          className={`px-3 py-1 mx-1 ${
            page === 1
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          ««
        </button>
        <button
          onClick={() => handlePageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className={`px-3 py-1 mx-1 ${
            page === 1
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          «
        </button>
        {[...Array(endPage - startPage + 1)].map((_, i) => (
          <button
            key={startPage + i}
            onClick={() => handlePageChange(startPage + i)}
            className={`px-3 py-1 mx-1 ${
              page === startPage + i
                ? "bg-blue-600 text-white"
                : theme === "dark"
                ? "bg-gray-700 text-white hover:bg-gray-600"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
          >
            {startPage + i}
          </button>
        ))}
        <button
          onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className={`px-3 py-1 mx-1 ${
            page === totalPages
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          »
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={page === totalPages}
          className={`px-3 py-1 mx-1 ${
            page === totalPages
              ? "bg-gray-400"
              : theme === "dark"
              ? "bg-gray-700 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          } disabled:opacity-50`}
        >
          »»
        </button>
      </div>
    );
  };

  const renderOrderCell = (fornitore, cliente) => {
    const isGreaterThanZero = parseFloat(fornitore) - parseFloat(cliente) > 0;
    return (
      <div className="flex items-center">
        <span
          className={`inline-block w-4 h-4 rounded-full mr-2 ${
            isGreaterThanZero ? "bg-green-500" : "bg-red-500"
          } flex items-center justify-center text-white`}
        ></span>
        <span>{fornitore}</span>
      </div>
    );
  };

  const switchDeposito = (deposito) => {
    switch (deposito) {
      case "C1":
        return "CASORIA";
      case "D1":
        return "CASERTA";
      case "U1":
        return "PESARO";
      case "P1":
        return "PESCARA";
      case "T1":
        return "BARBERINO";
      default:
        return deposito;
    }
  };

  const switchLetter = (lettera) => {
    switch (lettera) {
      case "S":
        return "Si";
      case "N":
        return "No";
      default:
        return lettera;
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        article: null,
        codice_agente: selectedAgent?.value || null,
        magazzino: null,
        codice_articolo: null,
      }}
      onSubmit={() => {}}
    >
      {({ setFieldValue, values }) => (
        <Form>
          <div className="py-8 pb-24 mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex justify-center items-center h-screen">
                <BeatLoader color="#2563eb" />
              </div>
            ) : (
              <div className="space-y-8 px-5 sm:px-12 mx-auto flex flex-col justify-between mb-12 mt-[50px] md:mt-50">
                <h2 className="hidden sm:block text-4xl text-blue-500 sm:max-w-72 font-bold sm:mb-24">
                  Disponibilità
                </h2>

                <div className="filters-container flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
                  <div className="cu-wi-se-3 min-h-c  dark:border-neutral-400">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions // This will use the default loadOptions even when no input
                      loadOptions={async (inputValue) => {
                        if (!inputValue.trim()) {
                          // If empty input, use the initial articles list (first 100)
                          return allArticles.map((article) => ({
                            value: article.CODICE_ARTICOLO,
                            label: `${article.DESCRIZIONE_ARTICOLO} (${article.CODICE_ARTICOLO})`,
                            article: article,
                          }));
                        }

                        // Otherwise search by descrizione_articolo
                        const articles = await searchArticles(inputValue);

                        return articles.map((article) => ({
                          value: article.CODICE_ARTICOLO,
                          label: `${article.DESCRIZIONE_ARTICOLO} (${article.CODICE_ARTICOLO})`,
                          article: article,
                        }));
                      }}
                      placeholder="Cerca articolo (descrizione)"
                      value={values.article}
                      onChange={(val) => {
                        setFieldValue(
                          "article",
                          val
                            ? {
                                value: val.value,
                                label: val.label,
                                article: val.article,
                              }
                            : null
                        );
                      }}
                      isClearable
                      classNames={{
                        container: () => "w-full md:w-auto",
                        control: () => "min-h-c border dark:border-neutral-400",
                        input: () => "!text-inherit",
                        singleValue: () =>
                          "text-neutral-900 dark:text-neutral-100",
                      }}
                      styles={{
                        // Style definitions remain the same
                        control: (base, state) => ({
                          ...base,
                          backgroundColor: "transparent",
                          borderColor:
                            theme === "dark" ? "#525252" : base.borderColor,
                          "&:hover": {
                            borderColor:
                              theme === "dark" ? "#404040" : base.borderColor,
                          },
                        }),
                        input: (base) => ({
                          ...base,
                          color: "inherit",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: theme === "dark" ? "#e5e5e5" : base.color,
                        }),
                        menuList: (base) => ({
                          ...base,
                          backgroundColor:
                            theme === "dark" ? "#404040" : base.backgroundColor,
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor:
                            theme === "dark"
                              ? state.isFocused
                                ? "#2563eb"
                                : "#404040"
                              : state.isFocused
                              ? base.backgroundColor
                              : base.backgroundColor,
                          "&:active": {
                            backgroundColor:
                              theme === "dark"
                                ? "#1d4ed8"
                                : base.backgroundColor,
                          },
                        }),
                        indicatorSeparator: () => ({
                          display: "none",
                        }),
                        dropdownIndicator: (base) => ({
                          ...base,
                          color: theme === "dark" ? "#e5e5e5" : "#525252",
                          "&:hover": {
                            color: theme === "dark" ? "#d4d4d4" : "#404040",
                          },
                        }),
                        clearIndicator: (base) => ({
                          ...base,
                          color: theme === "dark" ? "#e5e5e5" : "#525252",
                          "&:hover": {
                            color: theme === "dark" ? "#d4d4d4" : "#404040",
                          },
                        }),
                      }}
                      noOptionsMessage={() => "Nessun articolo trovato"}
                      loadingMessage={() => "Ricerca in corso..."}
                    />
                  </div>

{/*                   <div className="cu-wi-se-3 min-h-c dark:border-neutral-400">
                    <ArticleCodeSelect
                      value={values.codice_articolo}
                      onChange={(val) => setFieldValue("codice_articolo", val)}
                      theme={theme}
                      allArticles={allArticles} 
                    />
                  </div> */}

                  {/* Nel componente Select del magazzino e il suo messaggio di errore */}
                  <div className="cu-wi-se-3 min-h-c dark:border-neutral-400">
                    <Select
                      options={warehouseOptions}
                      placeholder={
                        !values.article && !values.codice_articolo
                          ? "Seleziona prima un articolo"
                          : "Seleziona Magazzino"
                      }
                      value={values.magazzino}
                      onChange={(val) => {
                        setFieldValue("magazzino", val);
                        if (unfilteredData && unfilteredData.length > 0) {
                          if (val) {
                            // Filtriamo mappando il codice al nome completo
                            const filteredData = unfilteredData.filter((item) => {
                              const itemMagazzino = item.MAGAZZINO?.toUpperCase() || "";
                              const selectedValue = val.value?.toUpperCase() || "";
                              
                              // Mappa C1 -> CASORIA, D1 -> CASERTA, etc.
                              const mappedValue = switchDeposito(selectedValue);
                              
                              return itemMagazzino === mappedValue.toUpperCase();
                            });
                            setAvailabilityData(filteredData);
                          } else {
                            // Se non c'è selezione, mostra tutti i dati
                            setAvailabilityData(unfilteredData);
                          }
                        }
                      }}
                      isClearable
                      classNames={{
                        container: () => "w-full md:w-auto",
                        control: () => "min-h-c border dark:border-neutral-400",
                      }}
                    />
                    {values.magazzino &&
                      !values.article &&
                      !values.codice_articolo && (
                        <div className="text-red-500 text-sm mt-1">
                          Seleziona anche un articolo (tramite codice o
                          descrizione)
                        </div>
                      )}
                  </div>
                  <button
                    type="button"
                    onClick={() => clearFilters(setFieldValue)}
                    className="min-h-c py-2 px-4 bg-blue-600 text-white font-semibold hover:bg-blue-700"
                  >
                    Cancella filtri
                  </button>
                  <button
                    type="button"
                    onClick={handleSearch}
                    className="min-h-c py-2 px-4 bg-blue-600 text-white font-semibold hover:bg-blue-700"
                  >
                    Cerca
                  </button>


                </div>

                {availabilityData && (
                  <>
                    <div
                      className="relative table-container custom-scrollbar"
                      ref={tableRef}
                    >
                      <table className="w-full min-w-max">
                        <thead>
                          <tr>
                            <th>Codice Articolo</th>
                            <th>Descrizione</th>
                            <th>Magazzino</th>
                            <th>Q.tà Disponibile</th>
                            <th>Promo</th>
                            <th>Outlet</th>
                            <th>Bloccato</th>
                            <th>Critico</th>
                            <th>Prezzo Netto</th>
                            {userRole === "supervisor" && <th>Ord. Cliente</th>}
                            <th>Ord. Fornitore</th>
                          </tr>
                        </thead>

                        <tbody>
                          {availabilityData.map((item, index) => (
                            <tr key={index}>
                              <td>{item.CODICE_ARTICOLO.trim()}</td>
                              <td>{item.DESCRIZIONE_ARTICOLO}</td>
                              <td>{item.MAGAZZINO}</td>
                              <td>{item.QUANTITA_DISPONIBILE}</td>
                              <td>{switchLetter(item.ARTICOLO_PROMO)}</td>
                              <td>{switchLetter(item.ARTICOLO_OUTLET)}</td>
                              <td>{switchLetter(item.ARTICOLO_BLOCCATO)}</td>
                              <td>{switchLetter(item.ARTICOLO_CRITICO)}</td>
                              <td>{item.PREZZO_NETTO}</td>
                              {userRole === "supervisor" && (
                                <td>{item.ORDINATO_CLIENTE}</td>
                              )}
                              <td>
                                {userRole === "supervisor"
                                  ? // Per i supervisor mostriamo pallino e numero seguendo la logica
                                    renderOrderCell(
                                      item.ORDINATO_FORNITORE,
                                      item.ORDINATO_CLIENTE
                                    )
                                  : // Per i non supervisor, calcoliamo la logica per il colore ma mostriamo solo il pallino
                                    (() => {
                                      const diff =
                                        parseFloat(item.ORDINATO_FORNITORE) -
                                        parseFloat(item.ORDINATO_CLIENTE);
                                      const isGreaterThanZero = diff > 0;
                                      return (
                                        <span
                                          className={`inline-block w-4 h-4 rounded-full ${
                                            isGreaterThanZero
                                              ? "bg-green-500"
                                              : "bg-red-500"
                                          }`}
                                        ></span>
                                      );
                                    })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <Pagination />
                  </>
                )}
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
}
